"use client";

import { plural } from "@lingui/core/macro";
import { useLingui } from "@lingui/react/macro";
import { Alert } from "@offstreetparking/design-system/feedback";
import { Text } from "@offstreetparking/design-system/typography";

export const VehicleChangesRemainingNote = ({
	changesRemaining,
	pendingChanges,
	pendingRemovals,
}: {
	changesRemaining: number;
	pendingChanges: number;
	pendingRemovals: number;
}) => {
	const { t } = useLingui();
	const lockedOutMessage = t({
		message: "After that, you can't add or edit vehicles on this permit.",
		comment:
			"Body of the warning above the Save button in the edit permit vehicles dialog when the permit is about to run out of self-service vehicle changes",
	});
	if (changesRemaining === 0) {
		if (pendingChanges === 0 && pendingRemovals > 0) {
			return (
				<Alert
					type="neutral"
					size="sm"
					title={t({
						message: "Saving won't use any changes",
						comment:
							"Title of the note above the Save button in the edit permit vehicles dialog when the permit has no self-service vehicle changes left and the parker has only removed vehicles, which doesn't use a change",
					})}
					message={t({
						message: "This permit has no vehicle changes left, so you can't add or edit vehicles.",
						comment:
							"Body of the note above the Save button in the edit permit vehicles dialog when the permit has no self-service vehicle changes left and the parker has only removed vehicles",
					})}
				/>
			);
		}
		return (
			<Alert
				type="neutral"
				size="sm"
				title={t({
					message: "No vehicle changes left",
					comment:
						"Title of the note above the Save button in the edit permit vehicles dialog when the permit has used all of its self-service vehicle changes",
				})}
				message={t({
					message: "You can't add or edit vehicles on this permit.",
					comment:
						"Body of the note above the Save button in the edit permit vehicles dialog when the permit has used all of its self-service vehicle changes",
				})}
			/>
		);
	}
	if (pendingChanges > changesRemaining) {
		return (
			<Alert
				type="warning"
				size="sm"
				title={t({
					message: plural(changesRemaining, {
						one: "These edits need more than the 1 vehicle change left",
						other: "These edits need more than the # vehicle changes left",
					}),
					comment:
						"Title of the warning above the Save button in the edit permit vehicles dialog when the vehicles about to be saved need more self-service vehicle changes than the permit has left",
				})}
				message={t({
					message: "Undo some edits to save.",
					comment:
						"Body of the warning above the Save button in the edit permit vehicles dialog when the vehicles about to be saved need more self-service vehicle changes than the permit has left",
				})}
			/>
		);
	}
	if (pendingChanges >= changesRemaining) {
		return (
			<Alert
				type="warning"
				size="sm"
				title={t({
					message: plural(pendingChanges, {
						one: "Saving uses your last vehicle change",
						other: "Saving uses your last # vehicle changes",
					}),
					comment:
						"Title of the warning above the Save button in the edit permit vehicles dialog when the vehicles about to be saved use up the permit's remaining self-service vehicle changes",
				})}
				message={lockedOutMessage}
			/>
		);
	}
	if (changesRemaining === 1) {
		return (
			<Alert
				type="warning"
				size="sm"
				title={t({
					message: "Only 1 vehicle change left",
					comment:
						"Title of the warning above the Save button in the edit permit vehicles dialog when the permit has one self-service vehicle change left",
				})}
				message={lockedOutMessage}
			/>
		);
	}
	const title =
		pendingChanges > 0
			? t({
					message: `Saving uses ${pendingChanges} of your ${changesRemaining} changes left`,
					comment:
						"Note above the Save button in the edit permit vehicles dialog: how many of the permit's remaining self-service vehicle changes the vehicles about to be saved use",
				})
			: t({
					message: plural(changesRemaining, {
						one: "# vehicle change left on this permit",
						other: "# vehicle changes left on this permit",
					}),
					comment:
						"Note above the Save button in the edit permit vehicles dialog: how many self-service vehicle changes the permit has left",
				});
	if (changesRemaining <= 3) {
		return (
			<Alert
				type="neutral"
				size="sm"
				title={title}
				message={t({
					message: "Each new or edited vehicle you save onto this permit counts as 1 change.",
					comment:
						"Body of the note above the Save button in the edit permit vehicles dialog when the permit has only a few self-service vehicle changes left",
				})}
			/>
		);
	}
	return (
		<Text size="sm" intent="muted" className="text-center">
			{title}
		</Text>
	);
};
